import axios from 'axios';
import { 
  RateConfigurationResponse, 
  RatingsDashboardResponse, 
  GoogleReview 
} from '../types';
import { API_ENDPOINTS } from '../config/apiConfig';

class ApruvRateService {
  private rateConfig?: RateConfigurationResponse;
  private activeRequests: Map<string, Promise<any>> = new Map(); // Cache for ongoing requests
  private configCacheKey?: string; // Track current config cache key

  async getConfig(companyId: number, locationId: number): Promise<RateConfigurationResponse> {
    const cacheKey = `config-${companyId}-${locationId}`;
    
    // Return cached data if available for the same company/location
    if (this.rateConfig && this.configCacheKey === cacheKey) {
      return this.rateConfig;
    }
    
    // Return existing request if in progress
    if (this.activeRequests.has(cacheKey)) {
      return this.activeRequests.get(cacheKey);
    }
    
    try {
      const requestPromise = axios.get<RateConfigurationResponse>(
        API_ENDPOINTS.RATE.CONFIG(),
        {
          headers: this.setHeaders(companyId, locationId)
        }
      ).then(response => {
        this.rateConfig = response.data;
        this.configCacheKey = cacheKey;
        this.activeRequests.delete(cacheKey);
        return response.data;
      });
      
      // Cache the ongoing request
      this.activeRequests.set(cacheKey, requestPromise);
      
      return await requestPromise;
    } catch (error) {
      this.activeRequests.delete(cacheKey);
      console.error('Error fetching rate config:', error);
      throw error;
    }
  }

  async updateConfig(companyId: number, locationId: number, payload: any): Promise<any> {
    try {
      const response = await axios.put(
        API_ENDPOINTS.RATE.UPDATE_CONFIG(),
        payload,
        {
          headers: this.setHeaders(companyId, locationId)
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating rate config:', error);
      throw error;
    }
  }

  async getRatingsDashboard(
    locationIds: string, 
    startDate: string, 
    endDate: string
  ): Promise<RatingsDashboardResponse> {
    try {
      const response = await axios.get<RatingsDashboardResponse>(
        API_ENDPOINTS.REVIEW_APP.DASHBOARD(locationIds, startDate, endDate)
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching ratings dashboard:', error);
      throw error;
    }
  }

  async getGoogleReviews(
    locationIds: string, 
    startDate: string, 
    endDate: string
  ): Promise<GoogleReview[]> {
    try {
      const response = await axios.get<GoogleReview[]>(
        API_ENDPOINTS.REVIEW_APP.GOOGLE_REVIEWS(locationIds, startDate, endDate)
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching Google reviews:', error);
      throw error;
    }
  }

  async getFeedbackDetails(
    locationIds: string, 
    startDate: string, 
    endDate: string
  ): Promise<any> {
    try {
      const response = await axios.get(
        API_ENDPOINTS.RATE.FEEDBACK_DETAILS(locationIds, startDate, endDate)
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching feedback details:', error);
      throw error;
    }
  }

  async feedbackActions(data: any): Promise<any> {
    try {
      const response = await axios.post(API_ENDPOINTS.RATE.FEEDBACK_ACTIONS(), data);
      return response.data;
    } catch (error) {
      console.error('Error performing feedback actions:', error);
      throw error;
    }
  }

  async generateReply(prompt: string): Promise<any> {
    try {
      const response = await axios.post(
        API_ENDPOINTS.RATE.GENERATE_REPLY(),
        { Review: prompt }
      );
      return response.data;
    } catch (error) {
      console.error('Error generating reply:', error);
      throw error;
    }
  }

  async sendReply(data: any): Promise<any> {
    try {
      const response = await axios.post(
        API_ENDPOINTS.RATE.SEND_REPLY(),
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error sending reply:', error);
      throw error;
    }
  }

  async searchCustomers(searchText: string, companyId?: number, locationId?: number): Promise<any> {
    try {
      const company = companyId || this.getCompanyIdFromLocalStorage();
      const location = locationId || this.getLocationIdFromParams();
      
      const response = await axios.post(
        API_ENDPOINTS.RATE.SEARCH_CUSTOMERS(),
        { search_text: searchText },
        {
          headers: this.setHeaders(company, location)
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error searching customers:', error);
      throw error;
    }
  }

  // Helper methods
  private setHeaders(companyId: number, locationId: number): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'Company-ID': companyId.toString(),
      'Location-ID': locationId.toString(),
    };
  }

  private getCompanyIdFromLocalStorage(): number {
    // Implementation would depend on your storage strategy
    return parseInt(localStorage.getItem('companyId') || '1', 10);
  }

  private getLocationIdFromParams(): number {
    // Implementation would depend on your routing strategy
    return parseInt(new URLSearchParams(window.location.search).get('locationId') || '1', 10);
  }

  // Getters and Setters
  getRateConfig(): RateConfigurationResponse | undefined {
    return this.rateConfig;
  }

  setRateConfig(config: RateConfigurationResponse): void {
    this.rateConfig = config;
  }
}

export const apruvRateService = new ApruvRateService();
export default apruvRateService;